import { action, computed, observable } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import { SystemUserType } from '~/types/model.types';

let browser_tab_id: string;

export abstract class BaseSessionStore {
    @observable currUserId: string;
    @observable sessionCompanyId: string;
    @observable sessionUserType: SystemUserType;
    private readonly browserTabIdStorageKey = 'browser_tab_id';

    private checkSessionStorageAvailable() {
        try {
            const testKey = '__test_key__';
            sessionStorage.setItem(testKey, 'test');
            sessionStorage.removeItem(testKey);
            return true;
        } catch (error) {
            return false;
        }
    }

    storeTabIdForRefreshOrNavigateAway = () => {
        if (this.checkSessionStorageAvailable()) {
            sessionStorage.setItem(this.browserTabIdStorageKey, browser_tab_id);
        }
        return null;
    };

    setBrowserTabId(tabId: string) {
        browser_tab_id = tabId;
        window.addEventListener(
            'pagehide',
            this.storeTabIdForRefreshOrNavigateAway
        );
    }

    get browserTabId() {
        // we use global scope browser_tab_id so that new instances of base-session will reuse the same tab id.
        // we use sessionStorage to keep the tab id when the page is refreshed or navigated away and back again within the same tab.
        if (browser_tab_id) {
            return browser_tab_id;
        }
        if (this.checkSessionStorageAvailable()) {
            const storedId = sessionStorage.getItem(
                this.browserTabIdStorageKey
            );
            if (storedId) {
                this.setBrowserTabId(storedId);
                sessionStorage.removeItem(this.browserTabIdStorageKey);
                return browser_tab_id;
            }
        }
        this.setBrowserTabId(uuidv4());
        return browser_tab_id;
    }

    @action
    setCurrUserId(userId: string) {
        this.currUserId = userId;
    }

    @action
    setCurrCompanyId(companyId: string) {
        this.sessionCompanyId = companyId;
    }

    get currCompanyId() {
        return this.sessionCompanyId;
    }

    @action
    init() {
        this.sessionCompanyId = null;
        this.currUserId = null;
        this.sessionUserType = null;
    }

    @computed
    get authenticated() {
        return !!this.currUserId;
    }

    @computed
    get isAuthenticating() {
        return false;
    }

    @computed
    get weakAuthUser() {
        return false;
    }

    @computed
    get weakAuthenticated() {
        return false;
    }

    @computed
    get limitedAuthStorageKey() {
        return '';
    }

    @action.bound
    setSessionUserType(userType: SystemUserType) {
        this.sessionUserType = userType;
    }
}
